import { Link } from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import React, { useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { Divider, FlexBox } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { Text } from 'shared/components/Text/Text'
import { useUserV3 } from 'shared/hooks/query/useUserV3'
import { ProfileHeaderDriverStatus } from 'shared/pages/ProfilePage/Profile/ProfileHeaderDriverStatus'
import { OverviewSafetyScore } from 'shared/pages/ProfilePage/ProfileOverview/OverviewSafetyScore'
import {
  SafetyScoreCards,
  SafetyScoreCardsType,
} from 'shared/pages/ProfilePage/ProfileOverview/SafetyScoreCards'
import { ProfilePageTab } from 'shared/pages/ProfilePage/ProfilePage'
import { lightGray1, royalBlue } from 'shared/theme/colors'
import styled from 'styled-components'

import { useHasPerformanceScores } from '../../../hooks/useHasFeatureFlag'
import { UserLink } from '../../User/UserLink'
import { SafetyToolUserCancellationRate } from './SafetyToolUserCancellationRate'
import { SafetyToolUserOnTimeRate } from './SafetyToolUserOnTimeRate'
import { SafetyToolUserSafetyScores } from './SafetyToolUserSafetyScores'

export function SafetyToolUser() {
  const history = useHistory()
  const { userId } = useParams<{ userId: string }>()
  const { user } = useUserV3(userId)
  const initialCard = window.location.hash.replace('#', '')?.split('?')[0]
  const [selectedCard, setSelectedCard] = useState<SafetyScoreCardsType>(
    initialCard === 'on-time-rate'
      ? SafetyScoreCardsType.ON_TIME_SERVICE_RATE
      : SafetyScoreCardsType.SAFETY_SCORE
  )
  const selectCard = (card: SafetyScoreCardsType) => {
    window.location.hash =
      card === SafetyScoreCardsType.ON_TIME_SERVICE_RATE ? 'on-time-rate' : 'safety-score'
    setSelectedCard(card)
  }
  const hasPerformanceScoresFlag = useHasPerformanceScores()

  return (
    <Container mb={4} mt={2} isColumn>
      {!hasPerformanceScoresFlag && (
        <>
          <Header alignItems="center" gap="20px" mb={5}>
            <Text variant="header2" fontColor={lightGray1}>
              Safety & Compliance
            </Text>
            <Text variant="header2">/</Text>
            <Text variant="header2">
              {user?.firstName} {user?.lastName}
            </Text>
            <Link href={`/users/${user?.id}/show#overview`} target="_blank">
              <OpenInNewIcon color="primary" fontSize="medium" />
            </Link>
          </Header>

          <OverviewSafetyScore user={user} isHeader />
        </>
      )}

      {hasPerformanceScoresFlag && (
        <FlexBox gap="8px" isColumn mb={5} alignItems="flex-start">
          <Button
            variant="text"
            size="small"
            icon={<ArrowBackIcon fontSize="small" />}
            onClick={() => history.push('/compliance-safety-tool')}
          >
            Safety & Performance
          </Button>

          <FlexBox mt={4} gap="16px">
            <Text variant="header2">
              {user?.firstName} {user?.lastName}
            </Text>
            {user && user?.driver?.isEarlyCycleDriver && <ProfileHeaderDriverStatus user={user} />}
          </FlexBox>

          <FlexBox>
            <UserLink
              user={{ id: userId, first_name: user?.firstName, last_name: user?.lastName }}
              sidebarOpener={
                <Text variant="header3" fontColor={royalBlue} styles="cursor: pointer;">
                  View driver profile
                </Text>
              }
              initialTab={ProfilePageTab.OVERVIEW}
            />

            <Divider vertical margin="0 16px" />

            <Link
              href={`https://cloudtruckslooker.cloud.looker.com/dashboards/184?User+ID=${userId}`}
              target="_blank"
              style={{ textDecoration: 'none' }}
            >
              <FlexBox gap="8px">
                <Text variant="header3" fontColor={royalBlue}>
                  See safety dashboard
                </Text>
                <OpenInNewIcon color="primary" fontSize="small" />
              </FlexBox>
            </Link>
          </FlexBox>
        </FlexBox>
      )}

      {hasPerformanceScoresFlag && (
        <SafetyScoreCards user={user} isHeader selectedCard={selectedCard} setSelectedCard={selectCard} />
      )}

      {selectedCard === SafetyScoreCardsType.SAFETY_SCORE && <SafetyToolUserSafetyScores userId={userId} />}

      {selectedCard === SafetyScoreCardsType.ON_TIME_SERVICE_RATE && (
        <SafetyToolUserOnTimeRate userId={userId} />
      )}

      {selectedCard === SafetyScoreCardsType.LATE_CANCEL_RATE && (
        <SafetyToolUserCancellationRate userId={userId} />
      )}
    </Container>
  )
}

const Container = styled(FlexBox)`
  max-width: 940px;
`

const Header = styled(FlexBox)`
  * {
    font-size: 20px !important;
  }
`
